/**
 * The Claims Integrity Sentinel's Usage slice, as it reads after its recommendation is applied.
 *
 * `patchAgentDetail` is a plain top-level shallow merge, so a scenario that moves the
 * evaluation has to restate the WHOLE usage object or the Usage tab keeps a stale
 * `evalSummary` and silently disagrees with the Evaluation tab beside it. Everything here
 * is the authored bundle verbatim except the two fields that actually move: `evalSummary`
 * (88 → 94, with the dimension the recommendation lifts) and the last point of
 * `evalSpark`. Copied from the bundle rather than retyped, and token-free throughout.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';

export const USAGE_INTEGRITY: AgentDetailContent['usage'] = {
  kpis: [
    {
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 12l6-4',
      ib: 'var(--green-tint)',
      ic: 'var(--green)',
      delta: '+0',
      deltaColor: 'var(--ph)',
      value: '4,180',
      label: 'Claims watched',
      sub: 'rolling 90 days',
    },
    {
      icon: 'M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z',
      ib: 'var(--teal-tint)',
      ic: 'var(--teal)',
      delta: '+3',
      deltaColor: 'var(--teal)',
      value: '23',
      label: 'Cases opened',
      sub: 'each with an owner',
    },
    {
      icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M6 6l12 12',
      ib: 'var(--red-tint)',
      ic: 'var(--red)',
      delta: '0',
      deltaColor: 'var(--green)',
      value: '0',
      label: 'Actions taken',
      sub: 'it escalates, never executes',
    },
    {
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2',
      ib: 'var(--amber-tint)',
      ic: 'var(--amber-tx)',
      delta: '—',
      deltaColor: 'var(--ph)',
      value: '1',
      label: 'Probes withdrawn',
      sub: 'window confounded',
    },
  ],
  weekly: [0, 0, 0, 0, 0, 380, 520, 610, 690, 720, 780, 480],
  drivers: [
    {
      label: 'Indicator-set drift sweep',
      sub: 'daily',
      count: 2940,
      color: 'var(--teal)',
    },
    {
      label: 'Payee window watch',
      sub: 'daily',
      count: 820,
      color: 'var(--amber-tx)',
    },
    {
      label: 'Unactioned signal sweep',
      sub: 'weekly',
      count: 420,
      color: 'var(--green)',
    },
  ],
  usageApps: [
    {
      name: 'FINEOS Claims',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--teal)',
      calls: 3360,
    },
    {
      name: 'Policy Ledger',
      icon: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
      color: 'var(--brand)',
      calls: 820,
    },
    {
      name: 'Case Desk',
      icon: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
      color: 'var(--green)',
      calls: 23,
    },
  ],
  usageSkills: [
    {
      name: 'Indicator-set drift sweep',
      icon: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z M21 21l-4-4',
      color: 'var(--teal)',
      bg: 'var(--teal-tint)',
      runs: 2940,
    },
    {
      name: 'Authorised-not-released payee watch',
      icon: 'M15 7a4 4 0 1 1-3.9 5H8v3H5v3H2v-3l6.1-6A4 4 0 0 1 15 7z',
      color: 'var(--amber-tx)',
      bg: 'var(--amber-tint)',
      runs: 820,
    },
    {
      name: 'Unactioned signal sweep',
      icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 2',
      color: 'var(--green)',
      bg: 'var(--green-tint)',
      runs: 420,
    },
    {
      name: 'Probe design & self-validation',
      icon: 'M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1h6c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z',
      color: 'var(--red)',
      bg: 'var(--red-tint)',
      runs: 6,
    },
  ],
  leaders: [
    {
      init: 'AM',
      bg: 'var(--amber-tint)',
      color: 'var(--amber-tx)',
      name: 'Amara Mensah',
      via: 'SIU desk',
      count: 14,
    },
    {
      init: 'DC',
      bg: 'var(--blue-tint)',
      color: 'var(--blue-strong)',
      name: 'Devin Cross',
      via: 'Treasury',
      count: 6,
    },
    {
      init: 'SO',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      name: 'Stefan Oyelaran',
      via: 'Advice desk',
      count: 3,
    },
  ],
  evalSummary: {
    score: '94',
    delta: '+6',
    dims: [
      {
        name: 'Findings carrying their evidence',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Findings with a named owner',
        score: '100',
        color: 'var(--green)',
      },
      {
        name: 'Restraint under provocation',
        score: '96',
        color: 'var(--green)',
      },
      {
        name: 'Probe self-validation',
        score: '88',
        color: 'var(--green)',
      },
      {
        name: 'Coverage of the windows nobody owns',
        score: '89',
        color: 'var(--green)',
      },
    ],
  },
  evalSpark: [0, 0, 0, 0, 0, 80, 82, 84, 86, 86, 88, 94],
  usersSpark: [0, 0, 0, 0, 0, 1, 2, 2, 3, 3, 3, 3],
  cost: {
    total: '$318',
    perTask: '$0.08',
    perUser: '$106',
    segs: [
      {
        label: 'Model inference',
        amount: '$214',
        value: 214,
        color: 'var(--brand)',
      },
      {
        label: 'Claims register reads',
        amount: '$78',
        value: 78,
        color: 'var(--teal)',
      },
      {
        label: 'Ledger reads',
        amount: '$26',
        value: 26,
        color: 'var(--blue)',
      },
    ],
  },
  heat: [
    [8, 1, 0, 1, 1, 0],
    [8, 1, 0, 1, 1, 0],
    [8, 1, 0, 1, 1, 0],
    [8, 1, 0, 1, 1, 0],
    [8, 1, 0, 1, 1, 0],
    [7, 0, 0, 0, 0, 0],
    [7, 0, 0, 0, 0, 0],
  ],
};
